import { type UseQueryResult } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import { Resource } from '../../../atoms/resource/Resource';
import { BaseSelectInput, type BaseSelectInputProps } from '../select-input/BaseSelectInput';

export type BaseGenericSelectProps<T> = Omit<BaseSelectInputProps<T>, 'data' | 'onSearch' | 'resource'> & {
	resource: UseQueryResult<Array<T>>;
	withoutInitialValue?: boolean;
};

export const BaseGenericSelect = <T,>({
	dropdownConfiguration,
	itemTemplate,
	labelProp,
	onChange,
	resource,
	value,
	withoutInitialValue,
	...props
}: BaseGenericSelectProps<T>) => {
	const [selectedItem, setSelectedItem] = useState<T | undefined>();

	useEffect(() => {
		if (value) {
			setSelectedItem(value);
		} else if (!withoutInitialValue) {
			const initialValue = resource?.data?.[0];

			setSelectedItem(initialValue);
			if (initialValue) {
				onChange?.(initialValue);
			}
		}
	}, [resource?.data, withoutInitialValue, value]);

	return (
		<Resource r={resource}>
			{data => (
				<BaseSelectInput
					data={data}
					dropdownConfiguration={dropdownConfiguration}
					itemTemplate={itemTemplate}
					labelProp={labelProp}
					value={selectedItem}
					withSearch={false}
					onChange={onChange}
					onSearch={i => i.filter(Boolean)}
					{...props}
				/>
			)}
		</Resource>
	);
};

BaseGenericSelect.displayName = 'BaseGenericSelect';
